import { useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import Collapse from "@material-ui/core/Collapse";
import Box from "@material-ui/core/Box";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";

dayjs.extend(utc);

const EVALUATION_TYPE_COLORS = {
  compliment: "#388e3c",
  suggestion: "#f57c00",
};

function Row({ log }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{log.id}</TableCell>
        <TableCell>{log.session_id || "-"}</TableCell>
        <TableCell>{log.subject_id || "-"}</TableCell>
        <TableCell>{log.question_id || "-"}</TableCell>
        <TableCell>
          <Chip
            label={log.evaluation_type}
            style={{
              backgroundColor: EVALUATION_TYPE_COLORS[log.evaluation_type] || "#9e9e9e",
              color: "white",
            }}
            size="small"
          />
        </TableCell>
        <TableCell>{log.model_used || "-"}</TableCell>
        <TableCell>${log.cost_usd || "0.00"}</TableCell>
        <TableCell>{log.created_at ? dayjs(log.created_at).format("YYYY-MM-DD HH:mm") : "-"}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: "bold" }}>User Answer</TableCell>
                    <TableCell style={{ fontWeight: "bold" }}>AI Response</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{log.user_answer || "-"}</TableCell>
                    <TableCell>{log.ai_response || "-"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Table size="small" style={{ marginTop: 16 }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: "bold" }}>Prompt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12, backgroundColor: "#f5f5f5", padding: 8 }}>
                        {log.prompt_used?.prompt || log.prompt_used?.system || "-"}
                      </pre>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Table size="small" style={{ marginTop: 16 }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: "bold" }}>Grounding Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12, backgroundColor: "#f5f5f5", padding: 8 }}>
                        {JSON.stringify(log.grounding_data, null, 2)}
                      </pre>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Table size="small" style={{ marginTop: 16 }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: "bold" }}>Subject ID</TableCell>
                    <TableCell style={{ fontWeight: "bold" }}>Session ID</TableCell>
                    <TableCell style={{ fontWeight: "bold" }}>Tokens</TableCell>
                    <TableCell style={{ fontWeight: "bold" }}>Cost</TableCell>
                    <TableCell style={{ fontWeight: "bold" }}>Model</TableCell>
                    <TableCell style={{ fontWeight: "bold" }}>Pricing Version</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{log.subject_id || "-"}</TableCell>
                    <TableCell>{log.session_id || "-"}</TableCell>
                    <TableCell>
                      Prompt: {log.prompt_tokens || 0}, Completion: {log.completion_tokens || 0}, Total: {log.total_tokens || 0}
                    </TableCell>
                    <TableCell>${log.cost_usd || "0.00"}</TableCell>
                    <TableCell>{log.model_used || "-"}</TableCell>
                    <TableCell>{log.pricing_version || "-"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function NudgeLogTable({ logs, isLoading }) {
  const { t } = useTranslation();

  const columns = [
    { id: "expand", label: "", width: "5%" },
    { id: "id", label: "ID", width: "5%" },
    { id: "session_id", label: "Session ID", width: "8%" },
    { id: "subject_id", label: "Subject ID", width: "8%" },
    { id: "question_id", label: "Question ID", width: "8%" },
    { id: "evaluation_type", label: "Type", width: "10%" },
    { id: "model_used", label: "Model", width: "12%" },
    { id: "cost_usd", label: "Cost (USD)", width: "8%" },
    { id: "created_at", label: "Created At", width: "12%" },
  ];

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table aria-label="Nudge Logs">
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell key={column.id} style={{ width: column.width }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                Loading...
              </TableCell>
            </TableRow>
          ) : logs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                No nudge logs found
              </TableCell>
            </TableRow>
          ) : (
            logs.map(log => <Row key={log.id} log={log} />)
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}